/**
 * Murmuration platform-module registry.
 *
 * One entry per module in the sidebar. This is the single source for the
 * navigation model, the header-bar section titles, and the copy rendered on
 * the placeholder screens for modules that are not yet built.
 *
 * Twenty-five modules in five groups, per "Information Architecture and
 * Navigation" (page 2497740865). Credit union staff see four groups and
 * twenty-one modules; `Platform` is Murmuration's own control plane and is
 * flagged `murmurationOnly` so the group can be gated. Hiding the nav entry
 * is presentation - the access control itself is server-side.
 *
 * Copy is taken verbatim from the MUR Confluence space, "Platform Modules"
 * (page 2446589953) and its children - `confluencePageId` records which page
 * each entry came from. Confluence is the source of truth for module scope;
 * when a module page changes, re-read it and update the entry here rather
 * than paraphrasing.
 *
 * `capabilities` quotes the source page's lead capability list. Two pages
 * spread their scope across several equal sections rather than one list
 * (Membership and Shares, Payroll and Employer Services, Product Management
 * Engine); those entries quote the page's section headings instead. Where a
 * page splits member-facing from staff-facing capability - Card Management
 * separates cardholder controls from card administration - the entry quotes
 * the staff-facing section, because this is a staff product. Every departure
 * is called out in a comment on the entry.
 *
 * Three entries do not map one-to-one onto a module page, and each says so:
 * Credit Union draws on three pages, Treasury takes its component list from a
 * child page, and Platform Insight is one row of the Reporting and
 * Intelligence report catalogue rather than a page of its own.
 *
 * "Open Banking Intelligence" (2446327830) has a module page and deliberately
 * no entry: that page describes an intelligence layer that "sits across the
 * whole platform rather than being a single feature", so it has no screen of
 * its own. Its capabilities surface as evidence panels inside Lending,
 * Collections, Financial Health and Reporting. Reaffirmed as closed question
 * C6 on "Open Design Questions" (page 2497806591).
 *
 * ASCII-only rule: the pound sign is written as the `\u00a3` escape so the
 * file stays 7-bit ASCII on disk while still rendering as a pound sign.
 */

/** Sidebar grouping, per the Information Architecture and Navigation page. */
export type NavGroupKey = 'operate' | 'money' | 'oversight' | 'configure' | 'platform'

export interface NavGroup {
  readonly key: NavGroupKey
  /** Rendered uppercase by the sidebar; stored in sentence case. */
  readonly label: string
  /** True for `platform` only - Murmuration's own control plane, which a
   *  credit union user must never see. Consumed by the sidebar to gate the
   *  group; the real control is server-side, so this is presentation. */
  readonly murmurationOnly?: boolean
}

export const NAV_GROUPS: readonly NavGroup[] = [
  { key: 'operate', label: 'Operate' },
  { key: 'money', label: 'Money' },
  { key: 'oversight', label: 'Oversight' },
  { key: 'configure', label: 'Configure' },
  { key: 'platform', label: 'Platform', murmurationOnly: true },
]

export interface ModuleDefinition {
  /** Stable key; also the React key in nav lists. */
  readonly key: string
  /** SPA route this module owns. */
  readonly route: string
  /** Sidebar label - shorter than `title` where the handoff shortens it. */
  readonly navLabel: string
  readonly group: NavGroupKey
  /** Page heading and header-bar section title. */
  readonly title: string
  /** One-line summary under the eyebrow. */
  readonly tagline: string
  readonly capabilities: readonly string[]
  /** Trailing qualifier shown below the capability list, where the source
   *  page carries one. */
  readonly note?: string
  readonly confluencePageId: string
}

/**
 * Modules with no screens yet. Each renders `ModulePlaceholderPage`.
 *
 * Declared in the order the Information Architecture page lists them, group
 * by group, so `navEntriesForGroup` needs no sort.
 *
 * Member Management is not here - it is the one built module, served by the
 * `/members` list and detail views. See `IMPLEMENTED_MODULES`.
 */
export const PLACEHOLDER_MODULES: readonly ModuleDefinition[] = [
  // ---------------------------------------------------------------- Operate
  {
    key: 'membership',
    route: '/membership',
    navLabel: 'Membership & Shares',
    group: 'operate',
    title: 'Membership and Shares',
    tagline:
      'Complete credit union membership management. This is fundamental to credit union ' +
      'operations and distinct from the CRM view of a member.',
    // The page has no lead capability list - its scope is six equal sections.
    // These are those section headings, verbatim.
    capabilities: [
      'Membership applications',
      'Membership rules',
      'Membership records',
      'Share management',
      'Dividend management',
      'Governance support',
    ],
    note: 'The platform manages a member through new, active, dormant, suspended and closed states.',
    confluencePageId: '2497806407',
  },
  {
    key: 'kyc',
    route: '/kyc',
    navLabel: 'KYC & Onboarding',
    group: 'operate',
    title: 'KYC and Onboarding',
    tagline: 'Secure, seamless, compliant onboarding, online or in branch.',
    capabilities: [
      'Identity verification through our partner Jumio.',
      'Partner credit referencing agency, with bulk purchase pricing.',
      'Fully compliant journeys with decision records and audit trails.',
    ],
    note:
      'The platform supports digital, assisted and manual verification, with review queues, ' +
      'document upload, staff notes, escalation and enhanced due diligence. Policy controls are ' +
      'set by the credit union within regulatory requirements. Estimated KYC cost is \u00a35 per ' +
      'customer.',
    confluencePageId: '2446262285',
  },
  {
    key: 'lending',
    route: '/lending',
    navLabel: 'Lending',
    group: 'operate',
    title: 'Lending and Decisioning',
    tagline:
      'A lending system the credit union controls. Policy, criteria, pricing, mandates and ' +
      'exceptions all belong to the credit union.',
    capabilities: [
      'Data from Open Banking, with income verification.',
      'Credit reference agency input. Estimated search cost \u00a31 to \u00a32.',
      'Income and expenditure verified on day one and in real time thereafter.',
      'Decision parameters set by the credit union.',
      'Manual override always available. Straightforward cases route quickly, complex cases get human attention.',
      "Instant loan payout into the member's current account on approval.",
    ],
    confluencePageId: '2446786561',
  },
  {
    key: 'business',
    route: '/business',
    navLabel: 'Business Banking',
    group: 'operate',
    title: 'Small Business Banking',
    tagline:
      'Business accounts for members who run sole trader or small businesses, and for community ' +
      'organisations.',
    // Capabilities are the page's "Features" list; "Who it serves" is the
    // audience, not the capability set.
    capabilities: [
      'Business current accounts.',
      'Business savings.',
      'SME lending.',
      'Multi-user access.',
      'Dual authorisation.',
      'Payment approval workflows.',
      'Business statements.',
      'Business cashflow analysis.',
    ],
    note:
      'Many members operate viable local businesses but struggle to get appropriately sized, ' +
      'fairly priced finance from mainstream providers. Murmuration supports a proportionate ' +
      'digital application, cash-flow assessment, document collection, decision workflow and ' +
      'ongoing monitoring.',
    confluencePageId: '2446491695',
  },
  {
    key: 'payroll',
    route: '/payroll',
    navLabel: 'Payroll & Employers',
    group: 'operate',
    title: 'Payroll and Employer Services',
    tagline:
      'Payroll lending, payroll savings and employer integrations. Employers are onboarded and ' +
      'linked to participating credit unions.',
    // No lead capability list - the page's scope is five equal sections.
    // These are those section headings, verbatim.
    capabilities: [
      'Payroll loans',
      'Payroll savings',
      'Employer integration',
      'Employer portal',
      'Employer reporting',
    ],
    note:
      'Deducting repayments at source reduces collections risk materially, which is the reason ' +
      'payroll lending sits at the centre of the lending growth case.',
    confluencePageId: '2497871958',
  },

  // ------------------------------------------------------------------ Money
  {
    key: 'savings',
    route: '/savings',
    navLabel: 'Savings & Deposits',
    group: 'money',
    title: 'Savings and Deposits',
    tagline: 'Full savings product support.',
    capabilities: [
      'Savings accounts, regular and fixed',
      'Christmas clubs',
      'Junior savings',
      'Prize savings',
      'Goal-based savings',
      'Payroll savings',
      'Budget or jam-jar accounts',
      'Cash ISAs',
      'Community savings products',
      'Business savings',
      'Fixed-term deposits where required',
    ],
    note:
      'Interest rates can be set at credit union or individual level. Auto Sweep from the member ' +
      'current account feeds savings automatically.',
    confluencePageId: '2446753793',
  },
  {
    key: 'payments',
    route: '/payments',
    navLabel: 'Banking & Payments',
    group: 'money',
    title: 'Banking and Payments',
    tagline: 'Full payment rails behind every member account.',
    capabilities: [
      'Faster Payments',
      'Direct debits',
      'Standing orders',
      'Free internal transfers',
      'Open Banking',
      'Interest paid at least monthly',
    ],
    note: 'Banking services are provided through regulated banking partners.',
    confluencePageId: '2446589974',
  },
  {
    key: 'cards',
    route: '/cards',
    navLabel: 'Cards',
    group: 'money',
    title: 'Card Management',
    tagline:
      'Complete card lifecycle management for members, businesses and credit unions. Cards are ' +
      'credit union branded, Visa or Mastercard.',
    // Capabilities are the page's "Card administration" list. "Card controls"
    // is explicitly the cardholder's in-app set, so it does not belong on a
    // staff screen.
    capabilities: [
      'Card issuance.',
      'Card replacement.',
      'Card cancellation.',
      'Card monitoring.',
      'Dispute management.',
      'Chargeback processing.',
    ],
    note:
      'Junior card controls sit with the parent or guardian, covering limits, freeze and ' +
      "transaction alerts. Business cards operate within the business account's user roles and " +
      'approval rules.',
    confluencePageId: '2497773596',
  },
  {
    key: 'collections',
    route: '/collections',
    navLabel: 'Collections',
    group: 'money',
    title: 'Collections and Member Support',
    tagline:
      "Collections designed to suit each credit union's requirements, with member support built in.",
    capabilities: [
      'Missed payment monitoring.',
      'Automated collection journeys.',
      'Payment arrangements.',
      'Vulnerability workflows, so members showing signs of difficulty get appropriate contact and forbearance early.',
      'Recoveries.',
    ],
    note:
      'Account patterns help identify when a member may need support before arrears escalate. ' +
      'This connects to the vulnerability markers in Member Management and the financial ' +
      'wellbeing intelligence in Reporting and Intelligence.',
    confluencePageId: '2445574156',
  },
  {
    key: 'ledger',
    route: '/ledger',
    navLabel: 'Ledger',
    group: 'money',
    title: 'Core Banking Ledger',
    tagline:
      'The single source of truth for all financial activity across savings, loans, current ' +
      'accounts, credit union operational accounts, settlement, treasury and reporting.',
    // Capabilities are the page's "Requirements" list.
    capabilities: [
      'Double-entry accounting.',
      'Real-time posting, with immediate balance update.',
      'Transaction journaling.',
      'Reconciliation.',
      'Reversals.',
      'Audit trails.',
      'Interest accruals.',
      'Fee processing and charge processing.',
      'Payroll deduction posting.',
      'Internal transfer posting.',
      'Clear posting rules for pending, booked, failed and reversed transactions.',
    ],
    note:
      'Every ledger entry must have a debit and a credit side. No balance can be manually ' +
      'amended without a controlled adjustment journal. All reversals must preserve the original ' +
      'transaction and create a linked correcting transaction.',
    confluencePageId: '2498002993',
  },
  {
    key: 'cu-accounts',
    route: '/cu-accounts',
    navLabel: 'CU Accounts',
    group: 'money',
    title: 'Credit Union Banking and Accounts',
    tagline:
      "Murmuration is the credit union's banking platform, not only the member app. Each credit " +
      'union gets its own banking facilities inside the ecosystem.',
    capabilities: [
      'Internal transfers.',
      'Faster Payments, inbound and outbound.',
      'Bulk payments.',
      'Supplier payments.',
      'Payroll payments.',
      'Immediate internal ecosystem payments.',
      'Real-time balances.',
      'Transaction history.',
      'Statements and exports.',
    ],
    note:
      'All internal transfers settle immediately and post to the ledger straight away, with no ' +
      'external delay. This covers member to credit union, credit union to member, credit union ' +
      'to credit union, and branch to branch.',
    confluencePageId: '2497904736',
  },
  {
    key: 'treasury',
    route: '/treasury',
    navLabel: 'Treasury',
    group: 'money',
    title: 'Treasury Marketplace',
    tagline:
      'Treasury management, optimisation and an institutional deposit marketplace, built into ' +
      'the platform as a core module.',
    // The Treasury Marketplace page is a parent with no capability list of its
    // own. These are the components from its child "How It Works" (2446819329).
    // Approval thresholds and the Board member / Auditor roles are on another
    // child, "Governance, Permissions and Audit" (2446786584).
    capabilities: [
      'Live treasury dashboard',
      'Treasury policy engine',
      'Live rate marketplace',
      'AI recommendation engine',
      'Approval workflow',
      'Electronic execution',
      'Maturity management',
      'Board reporting',
    ],
    note:
      'The module is built around one principle. Murmuration recommends, the credit union ' +
      'decides. Every credit union keeps ownership of its money, sets its own treasury policy, ' +
      'chooses and excludes counterparties, and approves every movement of funds.',
    confluencePageId: '2446622721',
  },

  // -------------------------------------------------------------- Oversight
  {
    key: 'compliance',
    route: '/compliance',
    navLabel: 'Compliance & Risk',
    group: 'oversight',
    title: 'Compliance and Risk',
    tagline:
      'Protect the members and the credit union. Built to meet FCA, PRA and PSR expectations ' +
      'with clear audit trails.',
    capabilities: [
      'AML and financial crime. Real-time monitoring, sanctions and PEP screening, due diligence.',
      'KYC and ongoing monitoring. Continuous risk assessment and periodic reviews.',
      'Consumer Duty and vulnerability. Spot vulnerability early and embed fair outcomes.',
      'Risk, audit and assurance. Enterprise risk management with full audit trails and reporting.',
      'Role-based access control and segregation of duties across the platform.',
    ],
    confluencePageId: '2445541398',
  },
  {
    key: 'fraud',
    route: '/fraud',
    navLabel: 'Fraud Prevention',
    group: 'oversight',
    title: 'Fraud Prevention',
    tagline:
      'Fraud prevention, detection, investigation and reporting. A dedicated capability, separate from AML.',
    // Capabilities are the page's "Detection" list. Fraud is its own module,
    // not part of Compliance - closed question C7 on Open Design Questions.
    capabilities: [
      'Mule account detection, using behavioural rules, payment velocity and new account monitoring.',
      'Account takeover detection, using device changes, login anomalies and payment behaviour changes, with step-up authentication.',
      'Fraud scoring.',
      'Payment fraud alerts.',
      'Lost or stolen card support.',
    ],
    note:
      'Fraud sits alongside AML but is a distinct function. Bringing it inside the platform ' +
      'removes a system, and gives fraud analysts the account, payment and device signals in one ' +
      'place.',
    confluencePageId: '2497773622',
  },
  {
    key: 'financial-health',
    route: '/financial-health',
    navLabel: 'Financial Health',
    group: 'oversight',
    title: 'Financial Health Engine',
    tagline:
      'An automated assessment engine for affordability, financial wellbeing, vulnerability and ' +
      'lending support.',
    // Capabilities are the page's "Scores generated" list.
    capabilities: [
      'Disposable income score.',
      'Affordability score.',
      'Vulnerability score.',
      'Financial health score.',
      'Savings opportunity score.',
    ],
    note:
      'Scores must be explainable. Automated scoring never removes the manual override available ' +
      'to lending staff. Vulnerability review happens before any collections action.',
    confluencePageId: '2498003014',
  },
  {
    key: 'reporting',
    route: '/reporting',
    navLabel: 'Reporting',
    group: 'oversight',
    title: 'Reporting and Intelligence',
    tagline:
      'Turn data into better decisions. Faster reporting, better decisions, less manual effort.',
    capabilities: [
      'Real-time operational dashboards.',
      'Custom report builder.',
      'Regulatory and board reporting.',
      'Member insight analytics.',
      'Lending performance monitoring and savings growth tracking.',
      'Arrears and collections intelligence.',
      'Forecasting and predictive analytics.',
      'Benefits panel.',
      'Export and API integration.',
    ],
    note:
      'Financial wellbeing intelligence. Monitor member financial resilience trends. Identify ' +
      'members showing signs of financial stress. Measure the impact of savings and lending ' +
      'initiatives. Demonstrate Consumer Duty outcomes. Support vulnerable members proactively.',
    confluencePageId: '2445541378',
  },
  {
    key: 'community',
    route: '/community',
    navLabel: 'Community Impact',
    group: 'oversight',
    title: 'Community Impact and Social Value',
    tagline:
      'Reporting and measurement of the community impact, financial inclusion and social value ' +
      'generated by participating credit unions. Intended as a strategic differentiator, not a ' +
      'reporting afterthought.',
    // Capabilities are the page's "Community impact reporting" list.
    capabilities: [
      'Members served.',
      'New members.',
      'Financial inclusion outcomes.',
      'Savings growth.',
      'Debt reduction outcomes.',
      'Payroll participation.',
    ],
    note:
      'Management of community funds, grant programmes, member assistance schemes and emergency ' +
      'support funds.',
    confluencePageId: '2498003036',
  },

  // -------------------------------------------------------------- Configure
  {
    key: 'products',
    route: '/products',
    navLabel: 'Products',
    group: 'configure',
    title: 'Product Management Engine',
    tagline:
      'A configurable product factory. Credit unions create, manage and launch products without ' +
      'software development, and control their own product offerings end to end.',
    // No lead capability list - the page's scope is three product families
    // plus the lifecycle. These are those section headings, verbatim.
    capabilities: [
      'Savings products',
      'Lending products',
      'Current account products',
      'Product lifecycle',
    ],
    note:
      'The rule for the platform is configuration, not code. If a credit union needs a code ' +
      'branch to launch a product, the design is wrong.',
    confluencePageId: '2497904760',
  },
  {
    key: 'credit-union',
    route: '/credit-union',
    navLabel: 'Credit Union',
    group: 'configure',
    title: 'Head Office and Branch Portals',
    tagline:
      'Tools for credit union head offices, branch networks, management teams and operational ' +
      'staff to run savings, lending, member servicing and reporting.',
    // This module draws on three pages. Head Office and Branch Portals
    // (2497806383, cited below) is the primary; staff users and roles come
    // from Roles and Permissions (2497773648), and rates, approval workflows,
    // common bond, KYC policy settings and branding from Multi-Tenancy and CU
    // Configuration (2447081495). Capabilities are the primary page's "Head
    // office portal" list.
    capabilities: [
      'Executive dashboards.',
      'Product management.',
      'Treasury management.',
      'Regulatory reporting.',
      'Member management.',
      'Branch management.',
      'User management.',
      'Rates, fees and product rule configuration.',
      'Board pack generation.',
      'APR setting at credit union, branch or individual member level, linked to credit income and expenditure reporting.',
    ],
    note:
      'Credit unions may operate as single-branch entities or with multiple branches. The ' +
      'platform supports both without a separate product. Branch staff see only their authorised ' +
      'members and workflows. Head office sees both consolidated and branch-level views.',
    confluencePageId: '2497806383',
  },
  {
    key: 'lexi',
    route: '/lexi',
    navLabel: 'Lexi',
    group: 'configure',
    title: 'Lexi AI',
    tagline:
      'Lexi is the AI assistant built into Murmuration, serving both members and credit union staff.',
    // Lexi itself is a docked copilot on every module, not a destination.
    // `Configure > Lexi` holds the per-credit-union knowledge base and
    // persona, which Multi-Tenancy and CU Configuration (2447081495) lists as
    // a tenant-varying setting. Capabilities are the page's only capability
    // list, "Voice services".
    capabilities: [
      'Inbound call handling.',
      'Member authentication before any sensitive information is disclosed.',
      'Call summarisation.',
      'Case creation.',
      'Call routing.',
      'Callback scheduling.',
      'Call transcripts.',
    ],
    note:
      'Lexi is not a staff replacement. The intention is to give staff better tools and reduce ' +
      'repetitive workload. Final decisions and accountability stay with authorised credit union ' +
      'staff, and human support remains available for members who need it.',
    confluencePageId: '2445672450',
  },

  // --------------------------------------------------------------- Platform
  // Murmuration's own control plane. Rendered only for Murmuration roles -
  // see `murmurationOnly` on the group.
  {
    key: 'platform-tenants',
    route: '/platform/tenants',
    navLabel: 'Tenants',
    group: 'platform',
    title: 'Multi-Tenancy and CU Configuration',
    // The source page opens with a status lozenge rather than a summary line,
    // so the tagline is the page's own statement of the rule it exists to
    // enforce. The page is PROPOSED and depends on ADR-002.
    tagline:
      'The rule is configuration, not code. If a credit union needs a code branch, the design is ' +
      'wrong.',
    capabilities: [
      'Branding, communications templates and website.',
      'Product catalogue and interest rates, at CU level or individual member level.',
      'Lending policy, criteria, mandates and exceptions.',
      'Treasury policy, approved and excluded counterparties, exposure limits, approval thresholds.',
      'Approval workflows and permissions.',
      'KYC policy settings within regulatory bounds.',
      'Lexi knowledge base and persona.',
      'Common bond and membership eligibility rules.',
    ],
    note:
      'What is shared. Application codebase, infrastructure, banking rails, KYC journeys, card ' +
      'platform. Regulatory controls that are not policy choices (sanctions screening, audit). ' +
      'National marketing and traffic routing.',
    confluencePageId: '2447081495',
  },
  {
    key: 'platform-integrations',
    route: '/platform/integrations',
    navLabel: 'Integrations',
    group: 'platform',
    title: 'Integrations and Third Parties',
    tagline: 'Named partners come from the proposition papers. Most selections are open.',
    // Capabilities are the Capability column of the page's platform-level
    // partners table - what this module manages, not who supplies it. The
    // page is PROPOSED and only Jumio is named.
    capabilities: [
      'Identity verification',
      'Credit referencing',
      'Banking / EMI rails',
      'Card scheme and issuing',
      'Open Banking (AIS)',
      'Sanctions and PEP screening',
      'SMS and email',
      'Treasury counterparties',
    ],
    note:
      'Every partner sits behind an interface we own (architecture principle 4). Partner outages ' +
      'must degrade gracefully. KYC falls back to manual review queues, payments queue with ' +
      'member-visible status. Per-partner exit plan documented, supporting the SS2/21 position.',
    confluencePageId: '2445771229',
  },
  {
    key: 'platform-support',
    route: '/platform/support',
    navLabel: 'Support',
    group: 'platform',
    title: 'Roles and Permissions',
    tagline:
      'Role-based access control across Murmuration, credit unions, branches, employers, SMEs ' +
      'and members. Users see only what they are allowed to see, bounded by tenant and by branch.',
    // Capabilities are the page's "Access control" list. The Murmuration
    // support role is defined on the same page: technical support, controlled
    // support access, integration monitoring and issue resolution, with
    // member data reachable only through audited, authorised support sessions.
    capabilities: [
      'Roles and permissions are assigned, not implied.',
      'Tenant boundaries and branch boundaries are enforced on every request.',
      'Privileged access requires MFA.',
      'Every permission change is audited.',
    ],
    note:
      'All financial, member, compliance and administrative actions are logged and cannot be ' +
      'altered. The log covers login activity, data access, record changes, approvals, payments ' +
      'and permission changes.',
    confluencePageId: '2497773648',
  },
  {
    key: 'platform-insight',
    route: '/platform/insight',
    navLabel: 'Platform Insight',
    group: 'platform',
    // This module has no page of its own. It is the Murmuration-management
    // row of the report catalogue on Reporting and Intelligence (2445541378),
    // which the `reporting` entry above also cites. The title is the module
    // name from the Information Architecture page rather than the page title,
    // because two screens titled "Reporting and Intelligence" would give the
    // breadcrumb and the window title no way to tell them apart.
    title: 'Platform Insight',
    tagline:
      'The reporting framework serves six audiences. Each row is a report set, not a single report.',
    // The Murmuration-management row, one report set per entry.
    capabilities: [
      'Revenue',
      'Growth',
      'Usage',
      'Transaction volumes',
      'Credit union onboarding and performance',
      'Platform and API performance',
      'Lexi usage',
      'Operational incidents',
    ],
    confluencePageId: '2445541378',
  },
]

export interface NavEntry {
  readonly key: string
  readonly route: string
  readonly label: string
  readonly group: NavGroupKey
  /** True when the route serves a real screen rather than a placeholder.
   *  Drives the leading-dot treatment on the nav item. */
  readonly implemented: boolean
}

/**
 * Modules that have real screens. Member Management is served by `/members`
 * over the SM2 account projection.
 */
const IMPLEMENTED_MODULES: readonly NavEntry[] = [
  {
    key: 'members',
    route: '/members',
    label: 'Members',
    group: 'operate',
    implemented: true,
  },
]

/** Full sidebar model, in handoff order: implemented first within each group. */
export const NAV_ENTRIES: readonly NavEntry[] = [
  ...IMPLEMENTED_MODULES,
  ...PLACEHOLDER_MODULES.map(
    (m): NavEntry => ({
      key: m.key,
      route: m.route,
      label: m.navLabel,
      group: m.group,
      implemented: false,
    }),
  ),
]

/** Nav entries belonging to one sidebar group, in declaration order. */
export function navEntriesForGroup(group: NavGroupKey): readonly NavEntry[] {
  return NAV_ENTRIES.filter((entry) => entry.group === group)
}

export function findPlaceholderModule(route: string): ModuleDefinition | undefined {
  return PLACEHOLDER_MODULES.find((m) => m.route === route)
}
